import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";
import type { LangCode } from "../../i18n";
import {
  Camera, CheckCircle2, ChevronDown, Clock, FolderOpen,
  MapPin, Mic, MicOff, Plus, Save, Upload, Wifi, WifiOff,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeployProject {
  id: string;
  hospital_name: string;
  status: string;
}

interface DeployCheckpoint {
  id: string;
  project_id: string;
  stop_number: number | null;
  floor: string | null;
  area: string | null;
  location_detail: string | null;
  placement_notes: string | null;
  qr_payload: string;
  status: string;
  captured_at: string | null;
  captured_lat: number | null;
  captured_lng: number | null;
  capture_photo_url: string | null;
}

interface CaptureData {
  checkpointId: string;
  lat: number | null;
  lng: number | null;
  photoDataUrl: string | null;
  floor: string;
  area: string;
  location_detail: string;
  notes: string;
  capturedBy: string;
  timestamp: string;
}

interface OfflineQueueItem {
  id: string;
  data: CaptureData;
  synced: boolean;
}

// ─── Auth Helpers ─────────────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const { token, user } = useAuthStore.getState();
  const h: Record<string, string> = { "Content-Type": "application/json" };
  const csrf = localStorage.getItem("csrfToken");
  if (csrf) h["x-csrf-token"] = csrf;
  if (token) h["Authorization"] = `Bearer ${token}`;
  if (user?.email) h["x-user-email"] = user.email;
  return h;
}

async function apiGet(action: string, extra = "") {
  const r = await fetch(`/api/deployments?action=${action}${extra}`, {
    headers: getAuthHeaders(), credentials: "include",
  });
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json();
}

async function apiPost(action: string, body: unknown) {
  const r = await fetch(`/api/deployments?action=${action}`, {
    method: "POST",
    headers: getAuthHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json();
}

// ─── Offline queue helpers ────────────────────────────────────────────────────

const QUEUE_KEY = "deploy_capture_queue";

function loadQueue(): OfflineQueueItem[] {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
}

function saveQueue(q: OfflineQueueItem[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function CaptureWalkthrough() {
  const { t, lang } = useTranslation();
  const user = useAuthStore(s => s.user);

  const [projects, setProjects] = useState<DeployProject[]>([]);
  const [activeProject, setActiveProject] = useState<DeployProject | null>(null);
  const [checkpoints, setCheckpoints] = useState<DeployCheckpoint[]>([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [capturingId, setCapturingId] = useState<string | null>(null);
  const [gpsStatus, setGpsStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [captureFloor, setCaptureFloor] = useState("");
  const [captureArea, setCaptureArea] = useState("");
  const [captureDetail, setCaptureDetail] = useState("");
  const [captureNotes, setCaptureNotes] = useState("");
  const [listening, setListening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showNewCkForm, setShowNewCkForm] = useState(false);
  const [newFloor, setNewFloor] = useState("1");
  const [newArea, setNewArea] = useState("");

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);

  // ── Online / offline tracking ─────────────────────────────────────────────
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline) syncOfflineQueue();
  }, [isOnline]);

  // ── Load projects ─────────────────────────────────────────────────────────
  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      const data = await apiGet("projects");
      setProjects((data.projects || []).filter((p: DeployProject) => p.status !== "complete"));
    } catch { /* offline, ignore */ }
  }

  async function selectProject(id: string) {
    try {
      const data = await apiGet("project", `&id=${id}`);
      setActiveProject(data.project);
      setCheckpoints(data.checkpoints || []);
      setShowProjectDropdown(false);
    } catch (e: any) {
      setError(e.message);
    }
  }

  // ── GPS capture ───────────────────────────────────────────────────────────
  function grabGps() {
    setGpsStatus("loading");
    navigator.geolocation.getCurrentPosition(
      pos => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsStatus("done");
      },
      () => setGpsStatus("error"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  // ── Camera capture ────────────────────────────────────────────────────────
  function handlePhotoCapture(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setPhotoDataUrl(ev.target?.result as string);
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  // ── Voice notes (Web Speech API) ──────────────────────────────────────────
  function toggleVoice() {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) { alert(t("deploy.speechNotSupported", "Speech recognition not supported in this browser.")); return; }

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const r = new SpeechRecognition();
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((res: any) => res[0].transcript)
        .join(" ");
      setCaptureNotes(prev => (prev ? prev + " " : "") + transcript);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    r.start();
    recognitionRef.current = r;
    setListening(true);
  }

  // ── Open capture form for a checkpoint ───────────────────────────────────
  function openCapture(ck: DeployCheckpoint) {
    setCapturingId(ck.id);
    setCoords(ck.captured_lat ? { lat: ck.captured_lat, lng: ck.captured_lng! } : null);
    setPhotoDataUrl(ck.capture_photo_url || null);
    setCaptureFloor(ck.floor || "");
    setCaptureArea(ck.area || "");
    setCaptureDetail(ck.location_detail || "");
    setCaptureNotes("");
    setGpsStatus(ck.captured_lat ? "done" : "idle");
    setError(null);
  }

  // ── Save capture ──────────────────────────────────────────────────────────
  async function saveCapture() {
    if (!capturingId || !activeProject) return;
    setSaving(true);
    setError(null);

    const captureData: CaptureData = {
      checkpointId: capturingId,
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      photoDataUrl,
      floor: captureFloor,
      area: captureArea,
      location_detail: captureDetail,
      notes: captureNotes,
      capturedBy: user?.name || user?.email || "unknown",
      timestamp: new Date().toISOString(),
    };

    if (!isOnline) {
      const queue = loadQueue();
      const existing = queue.findIndex(i => i.id === capturingId);
      const item: OfflineQueueItem = { id: capturingId, data: captureData, synced: false };
      if (existing >= 0) queue[existing] = item; else queue.push(item);
      saveQueue(queue);
      setCheckpoints(prev => prev.map(c =>
        c.id === capturingId
          ? { ...c, status: "captured", captured_lat: coords?.lat ?? null, captured_lng: coords?.lng ?? null, capture_photo_url: photoDataUrl, floor: captureFloor, area: captureArea, location_detail: captureDetail }
          : c
      ));
      setCapturingId(null);
      setSaving(false);
      return;
    }

    try {
      await apiPost("update-checkpoint", {
        id: capturingId,
        floor: captureFloor || null,
        area: captureArea || null,
        location_detail: captureDetail || null,
        placement_notes: captureNotes || null,
        captured_lat: coords?.lat ?? null,
        captured_lng: coords?.lng ?? null,
        capture_photo_url: photoDataUrl,
        status: "captured",
        captured_by: user?.name || user?.email || "unknown",
        captured_at: new Date().toISOString(),
      });

      setCheckpoints(prev => prev.map(c =>
        c.id === capturingId
          ? { ...c, status: "captured", captured_lat: coords?.lat ?? null, captured_lng: coords?.lng ?? null, capture_photo_url: photoDataUrl, floor: captureFloor, area: captureArea, location_detail: captureDetail }
          : c
      ));
      setCapturingId(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Sync offline queue ────────────────────────────────────────────────────
  async function syncOfflineQueue() {
    const queue = loadQueue().filter(i => !i.synced);
    if (queue.length === 0) return;
    for (const item of queue) {
      try {
        await apiPost("update-checkpoint", {
          id: item.data.checkpointId,
          floor: item.data.floor || null,
          area: item.data.area || null,
          location_detail: item.data.location_detail || null,
          placement_notes: item.data.notes || null,
          captured_lat: item.data.lat,
          captured_lng: item.data.lng,
          capture_photo_url: item.data.photoDataUrl,
          status: "captured",
          captured_by: item.data.capturedBy,
          captured_at: item.data.timestamp,
        });
        item.synced = true;
      } catch { /* will retry next time */ }
    }
    saveQueue(queue);
    // Refresh checkpoints if project is open
    if (activeProject) {
      try {
        const data = await apiGet("project", `&id=${activeProject.id}`);
        setCheckpoints(data.checkpoints || []);
      } catch { /* ignore */ }
    }
  }

  // ── Create new unplanned checkpoint ──────────────────────────────────────
  async function createNewCheckpoint(e: React.FormEvent) {
    e.preventDefault();
    if (!activeProject || !newArea.trim()) return;
    setSaving(true);
    try {
      const qrPayload = crypto.randomUUID();
      const nextStop = checkpoints.length > 0
        ? Math.max(...checkpoints.map(c => c.stop_number ?? 0)) + 1
        : 1;
      const data = await apiPost("create-checkpoint", {
        project_id: activeProject.id,
        floor: newFloor,
        area: newArea,
        stop_number: nextStop,
        qr_payload: qrPayload,
      });
      setCheckpoints(prev => [...prev, data.checkpoint]);
      setShowNewCkForm(false);
      setNewArea("");
      openCapture(data.checkpoint);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Counts ────────────────────────────────────────────────────────────────
  const capturedCount = checkpoints.filter(c => c.status !== "planned").length;
  const total = checkpoints.length;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-blue-600" />
          <h1 className="text-base font-semibold text-gray-900">{t("deploy.captureWalkthrough", "Capture Walkthrough")}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isOnline ? (
            <span className="flex items-center gap-1 text-xs text-green-600"><Wifi className="w-3 h-3" /> {t("status.online", "Online")}</span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-orange-500"><WifiOff className="w-3 h-3" /> {t("status.offline", "Offline")}</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Project selector */}
      <div className="px-4 py-3 bg-white border-b border-gray-100">
        <div className="relative">
          <button
            onClick={() => setShowProjectDropdown(!showProjectDropdown)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded border border-gray-300 bg-white w-full"
          >
            <FolderOpen className="w-4 h-4 text-gray-400" />
            <span className="flex-1 text-left truncate text-gray-700">
              {activeProject ? activeProject.hospital_name : t("deploy.selectProjectShort", "Select project…")}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          {showProjectDropdown && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              {projects.map(p => (
                <button
                  key={p.id}
                  onClick={() => selectProject(p.id)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 border-b border-gray-50 last:border-0"
                >
                  {p.hospital_name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {activeProject ? (
        <>
          {/* Progress bar */}
          <div className="px-4 py-3 bg-white border-b border-gray-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-600 font-medium">
                {t("deploy.capturedProgress", "{done} / {total} captured").replace("{done}", String(capturedCount)).replace("{total}", String(total))}
              </span>
              <span className="text-xs text-gray-400">
                {total > 0 ? Math.round((capturedCount / total) * 100) : 0}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-sm">
              <div
                className="h-2 bg-blue-500 rounded-sm transition-all"
                style={{ width: total > 0 ? `${(capturedCount / total) * 100}%` : "0%" }}
              />
            </div>
          </div>

          {/* Capture form (inline) */}
          {capturingId && (
            <CaptureForm
              checkpoint={checkpoints.find(c => c.id === capturingId)!}
              t={t}
              coords={coords}
              gpsStatus={gpsStatus}
              photoDataUrl={photoDataUrl}
              floor={captureFloor}
              area={captureArea}
              detail={captureDetail}
              notes={captureNotes}
              listening={listening}
              saving={saving}
              onGrabGps={grabGps}
              onPhoto={() => cameraInputRef.current?.click()}
              onFloor={setCaptureFloor}
              onArea={setCaptureArea}
              onDetail={setCaptureDetail}
              onNotes={setCaptureNotes}
              onVoice={toggleVoice}
              onSave={saveCapture}
              onCancel={() => setCapturingId(null)}
            />
          )}

          {/* Checkpoint list */}
          {!capturingId && (
            <div className="flex-1 overflow-y-auto">
              {/* Group by floor */}
              {[...new Set(checkpoints.map(c => c.floor || "?"))].sort().map(floor => (
                <div key={floor}>
                  <div className="sticky top-0 bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide z-10">
                    {t("deploy.floorN", "Floor {n}").replace("{n}", floor)}
                  </div>
                  {checkpoints
                    .filter(c => (c.floor || "?") === floor)
                    .sort((a, b) => (a.stop_number ?? 999) - (b.stop_number ?? 999))
                    .map(ck => (
                      <CaptureCheckpointRow
                        key={ck.id}
                        ck={ck}
                        t={t}
                        lang={lang}
                        onCapture={() => openCapture(ck)}
                      />
                    ))}
                </div>
              ))}

              {/* Add unplanned */}
              {showNewCkForm ? (
                <form onSubmit={createNewCheckpoint} className="px-4 py-3 bg-white border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">{t("deploy.newUnplannedCheckpoint", "New Unplanned Checkpoint")}</p>
                  <div className="flex gap-2 mb-2">
                    <input
                      value={newFloor}
                      onChange={e => setNewFloor(e.target.value)}
                      className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded"
                      placeholder={t("deploy.floor", "Floor")}
                    />
                    <input
                      value={newArea}
                      onChange={e => setNewArea(e.target.value)}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded"
                      placeholder={t("deploy.areaDescription", "Area / description")}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                      {t("deploy.createAndCapture", "Create & Capture")}
                    </button>
                    <button type="button" onClick={() => setShowNewCkForm(false)} className="px-3 py-1.5 text-xs border border-gray-300 rounded">
                      {t("common.cancel", "Cancel")}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowNewCkForm(true)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-200"
                >
                  <Plus className="w-4 h-4" /> {t("deploy.addUnplannedCheckpoint", "Add Unplanned Checkpoint")}
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          {t("deploy.selectProjectToCapture", "Select a project to start capturing.")}
        </div>
      )}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePhotoCapture}
      />
    </div>
  );
}

// ─── Capture Form ─────────────────────────────────────────────────────────────

function CaptureForm({
  checkpoint, t, coords, gpsStatus, photoDataUrl, floor, area, detail, notes,
  listening, saving, onGrabGps, onPhoto, onFloor, onArea, onDetail, onNotes,
  onVoice, onSave, onCancel,
}: {
  checkpoint: DeployCheckpoint;
  t: (key: string, fallback?: string) => string;
  coords: { lat: number; lng: number } | null;
  gpsStatus: string;
  photoDataUrl: string | null;
  floor: string; area: string; detail: string; notes: string;
  listening: boolean; saving: boolean;
  onGrabGps: () => void;
  onPhoto: () => void;
  onFloor: (v: string) => void;
  onArea: (v: string) => void;
  onDetail: (v: string) => void;
  onNotes: (v: string) => void;
  onVoice: () => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="bg-blue-50 border-b border-blue-200 px-4 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-blue-900">
          {t("deploy.stopNumber", "Stop #{n}").replace("{n}", String(checkpoint.stop_number ?? "?"))} — {checkpoint.area || t("deploy.newCheckpoint", "New checkpoint")}
        </p>
        <button onClick={onCancel} className="text-xs text-gray-500 hover:underline">{t("common.cancel", "Cancel")}</button>
      </div>

      {/* GPS */}
      <div className="flex items-center gap-2">
        <button
          onClick={onGrabGps}
          disabled={gpsStatus === "loading"}
          className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded ${
            gpsStatus === "done" ? "bg-green-600 text-white" :
            gpsStatus === "error" ? "bg-red-100 text-red-700 border border-red-300" :
            "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <MapPin className="w-3.5 h-3.5" />
          {gpsStatus === "loading" ? t("deploy.gettingGps", "Getting GPS…") :
           gpsStatus === "done" ? `${coords!.lat.toFixed(5)}, ${coords!.lng.toFixed(5)}` :
           gpsStatus === "error" ? t("deploy.gpsFailedRetry", "GPS failed — retry") :
           t("deploy.grabGps", "Grab GPS")}
        </button>
      </div>

      {/* Photo */}
      <div>
        <button
          onClick={onPhoto}
          className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <Camera className="w-3.5 h-3.5" />
          {photoDataUrl ? t("deploy.retakePhoto", "Retake Photo") : t("deploy.takePhoto", "Take Photo")}
        </button>
        {photoDataUrl && (
          <img src={photoDataUrl} alt="capture" className="mt-2 h-28 rounded object-cover border" />
        )}
      </div>

      {/* Fields */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">{t("deploy.floor", "Floor")}</label>
          <input value={floor} onChange={e => onFloor(e.target.value)}
            className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" />
        </div>
        <div>
          <label className="text-xs text-gray-500">{t("deploy.area", "Area")}</label>
          <input value={area} onChange={e => onArea(e.target.value)}
            className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500">{t("deploy.locationDetail", "Location Detail")}</label>
        <input value={detail} onChange={e => onDetail(e.target.value)}
          className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
          placeholder={t("deploy.locationDetailShort", "Left of main doors…")} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-0.5">
          <label className="text-xs text-gray-500">{t("deploy.notes", "Notes")}</label>
          <button onClick={onVoice} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${listening ? "bg-red-500 text-white animate-pulse" : "text-gray-500 hover:bg-gray-200"}`}>
            {listening ? <><MicOff className="w-3 h-3" /> {t("deploy.stopVoice", "Stop")}</> : <><Mic className="w-3 h-3" /> {t("deploy.voice", "Voice")}</>}
          </button>
        </div>
        <textarea value={notes} onChange={e => onNotes(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white resize-none"
          rows={2} placeholder={t("deploy.notesPlaceholder", "Landmark notes, mounting details…")} />
      </div>

      <button
        onClick={onSave}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
      >
        <Save className="w-4 h-4" />
        {saving ? t("common.saving", "Saving…") : t("deploy.saveCapture", "Save Capture")}
      </button>
    </div>
  );
}

// ─── Checkpoint Row ───────────────────────────────────────────────────────────

function CaptureCheckpointRow({
  ck, t, lang, onCapture,
}: {
  ck: DeployCheckpoint;
  t: (key: string, fallback?: string) => string;
  lang: LangCode;
  onCapture: () => void;
}) {
  const captured = ck.status !== "planned";
  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white ${captured ? "opacity-70" : ""}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${captured ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
        {captured ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : (ck.stop_number ?? "?")}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{ck.area || t("deploy.unnamed", "Unnamed")}</p>
        <p className="text-xs text-gray-500">{ck.location_detail || ck.placement_notes || ""}</p>
        {captured && ck.captured_at && (
          <p className="text-xs text-green-600 flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" />
            {new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(new Date(ck.captured_at))}
          </p>
        )}
      </div>
      <button
        onClick={onCapture}
        className={`px-3 py-1.5 text-xs rounded font-medium flex-shrink-0 ${
          captured
            ? "border border-gray-300 text-gray-500 hover:bg-gray-50"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
      >
        {captured ? t("deploy.recapture", "Re-capture") : t("deploy.capture", "Capture")}
      </button>
    </div>
  );
}
